import { PhoebeFlowerIcon } from '@apps/web/components/settings/phoebe_flower_icon';
import { ConversationAssigneeAvatar } from '@apps/web/components/shared/messages/conversation_assignment';
import { getConversationPreview } from '@apps/web/components/shared/messages/conversation_list_utils';
import {
    getConversationAvatarToneClass,
    UserAvatar,
} from '@apps/web/components/ui/user_avatar';
import { formatTypingLabel } from '@apps/web/hooks/use_conversation_typing';
import { useFormatPersonName } from '@apps/web/hooks/use_person_name_format';
import { formatInboxTimestamp } from '@apps/web/lib/date_utils';
import { cn } from '@apps/web/lib/utils';
import { readConversationOwnership } from '@apps/web/swr_data_fetching/conversation_ownership';
import { type OrganizationConversationMetadata } from '@libraries/typescript/api_client';

type ConversationListItemContentProps = {
    readonly conversation: OrganizationConversationMetadata;
    readonly timezone: string;
    readonly className?: string;
    readonly showOwnership?: boolean;
    readonly showPendingReviewIndicator?: boolean;
    // Production Conversations keeps the trailing 6px unread pip. The mockup
    // paints its own leading iMessage-style disc and passes false so the two
    // cannot stack.
    readonly showTrailingUnreadDot?: boolean;
    readonly hideAvatar?: boolean;
    // Teammates currently typing in this conversation; when present, their
    // "… is typing…" label replaces the message preview.
    readonly typingUsers?: ReadonlyArray<{ readonly displayName: string }>;
};

function conversationAvatarName(
    conversation: OrganizationConversationMetadata,
): string {
    const named =
        conversation.caregiver_name || conversation.external_contact_name;
    if (named) {
        return named;
    }
    const digits = conversation.phone_number.replace(/\D/g, '');
    if (digits.length >= 2) {
        return `${digits[digits.length - 2]} ${digits[digits.length - 1]}`;
    }
    return '?';
}

export function ConversationListItemContent({
    conversation,
    timezone,
    className,
    showOwnership = false,
    showPendingReviewIndicator = false,
    showTrailingUnreadDot = true,
    hideAvatar = false,
    typingUsers = [],
}: ConversationListItemContentProps) {
    const { formatName } = useFormatPersonName();
    const preview = getConversationPreview(conversation);
    const typingLabel = formatTypingLabel(typingUsers);
    const ownership = readConversationOwnership(conversation);
    const isUnread = conversation.unread_count > 0;
    const hasPendingReview = conversation.pending_staged_message_count > 0;
    const displayName =
        (conversation.caregiver_name
            ? formatName(conversation.caregiver_name)
            : null) ||
        conversation.external_contact_name ||
        conversation.phone_number;
    return (
        <div
            className={cn(
                hideAvatar
                    ? 'grid grid-cols-1 items-center gap-y-0.5'
                    : 'grid grid-cols-[auto_minmax(0,1fr)] items-center gap-x-2.5 gap-y-0.5',
                className,
            )}
        >
            {hideAvatar ? null : (
                <div className="row-span-2">
                    <UserAvatar
                        className="size-8"
                        colorKey={conversation.id}
                        displayName={conversationAvatarName(conversation)}
                        toneClassName={getConversationAvatarToneClass(
                            conversation.id,
                        )}
                    />
                </div>
            )}
            <div className="flex min-w-0 items-center justify-between gap-2">
                <div className="flex min-w-0 items-center gap-1">
                    <span
                        className={cn(
                            'text-foreground min-w-0 truncate text-sm',
                            isUnread && 'font-semibold',
                        )}
                    >
                        {displayName}
                    </span>
                    {showPendingReviewIndicator && hasPendingReview ? (
                        <span
                            aria-label="Pending review"
                            className="text-agent-action flex shrink-0 items-center gap-0.5 text-[10px] font-medium"
                        >
                            <PhoebeFlowerIcon className="size-2.5 shrink-0" />
                            <span aria-hidden="true">Pending</span>
                        </span>
                    ) : null}
                </div>
                <div className="flex shrink-0 items-center gap-1.5">
                    {showOwnership &&
                    ownership.assigned_to_user_id &&
                    ownership.assigned_to_user_name ? (
                        <ConversationAssigneeAvatar
                            userId={ownership.assigned_to_user_id}
                            name={ownership.assigned_to_user_name}
                        />
                    ) : null}
                    <span className="text-muted-foreground text-[11px] whitespace-nowrap">
                        {formatInboxTimestamp(
                            conversation.last_active_at,
                            timezone,
                        )}
                    </span>
                    {showTrailingUnreadDot && isUnread ? (
                        <span
                            aria-label="Unread"
                            className="size-1.5 shrink-0 rounded-full bg-blue-500"
                            role="img"
                        />
                    ) : null}
                </div>
            </div>
            {typingLabel ? (
                <p className="min-w-0 truncate text-xs font-medium text-blue-600">
                    {typingLabel}
                </p>
            ) : preview ? (
                <p className="text-muted-foreground min-w-0 truncate text-xs">
                    {preview.text}
                </p>
            ) : null}
        </div>
    );
}
